import React, {useEffect, useMemo, useState, memo} from 'react';
import {useTitle} from '../../components/General/Layout';
import {Box, FormControl, FormLabel, Input, Table, Thead, Tr, Th, Tbody, Td, Button, TableContainer} from '@chakra-ui/react';
import { FaLongArrowAltDown , FaLongArrowAltUp  } from "react-icons/fa";
import {useQuery, useMutation, useQueryClient} from 'react-query';
import {get, queryStringBuilder, post} from '../../service/ApiService';
import {Utility} from '../../service/Urls';
import {useReactTable, getCoreRowModel, flexRender, getSortedRowModel} from '@tanstack/react-table';
import {formatDate} from '../../helpers/Functions';
import ErrorHelper from '../../service/ErrorHelper';

export default function AREntry2() {
  const [_, setTitle] = useTitle('A/R Entry 2');

  const [toast, setToast] = useState(null);

  const [clientCode, setClientCode] = useState('');
  const [deptCode, setDeptCode] = useState('');

  const [saveData, setSaveData] = useState([]);

  const queryClient = useQueryClient();

  const {data} = useQuery(
    ['ar-entry-2', clientCode, deptCode],
    () => get(queryStringBuilder(Utility.AR_ENTRY + clientCode, {department: deptCode})),
    {
      onSuccess: response => {
        if (response.isSuccess) {
          setSaveData(response.data);
        } else {
          setSaveData([]);
        }
      },
      enabled: !!clientCode,
    }
  );

  const {mutate} = useMutation(data => post(Utility.AR_ENTRY + clientCode, data), {
    onSuccess: response => {
      if (response.isSuccess) {
        queryClient.invalidateQueries('ar-entry-2');

        setToast({successMessage: 'Save Successful!'});
      } else {
        setToast({response: {error: true}, errorMessage: response.error});
      }
    },
  });

  const onLoad = event => {
    event.preventDefault();

    const formData = new FormData(event.target);

    setClientCode(formData.get('clientCode'));
    setDeptCode(formData.get('deptCode'));
  };

  const onSubmit = () => {
    mutate(saveData);
  };

  useEffect(() => {
    setTitle('A/R Entry 2');
  }, []);

  return (
    <Box>
      <Box as="form" display="flex" alignItems="flex-end" gap={4} width={500} mb={4} onSubmit={onLoad}>
        <FormControl>
          <FormLabel>Client Code</FormLabel>
          <Input name="clientCode" />
        </FormControl>

        <FormControl>
          <FormLabel>Dept Code</FormLabel>
          <Input name="deptCode" />
        </FormControl>

        <Button type="submit" mt={3} width={125}>
          Load
        </Button>

        <Button type="button" colorScheme="teal" mt={3} onClick={onSubmit} width={125}>
          Save
        </Button>
      </Box>

      <ARTable data={data} setSaveData={setSaveData} />

      <ErrorHelper toastResponse={toast} />
    </Box>
  );
}

const onKeyDown = (index, getIndex) => e => {
  let id = null;
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    id = `${index - 1}-${getIndex()}`;
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    id = `${index}-${getIndex() - 1}`;
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    id = `${index + 1}-${getIndex()}`;
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    id = `${index}-${getIndex() + 1}`;
  }

  if (id) {
    const element = document.getElementById(id);
    element?.focus();
    element?.select();
  }
};

const DCT = {STRING: 0, NUMBER: 1, DATE: 2};
const defaultColumn = (type = DCT.STRING) => ({
  cell: ({row: {index, original}, column: {id, getIndex}, table}) => {
    const initialValue =
      type === DCT.STRING ? original[id]?.toString().trim() : type === DCT.NUMBER ? original[id] : formatDate(original[id]) ?? original[id];

    // We need to keep and update the state of the cell normally
    const [value, setValue] = useState(initialValue);

    // When the input is blurred, we'll call our table meta's updateData function
    const onBlur = () => {
      table.options.meta?.updateData(index, id, value);
    };

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    return (
      <input
        id={`${index}-${getIndex()}`}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={onBlur}
        onKeyDown={onKeyDown(index, getIndex)}
        onClick={e => e.target?.select()}
        type={type === DCT.NUMBER ? 'number' : 'text'}
        readOnly={id === 'emClient' || id === 'emCode' || id === 'emName'}
      />
    );
  },
});

const booleanColumn = {
  cell: ({row: {index, original}, column: {id, getIndex}, table}) => {
    const initialValue = original[id] ?? false;

    const [value, setValue] = useState(initialValue);

    const onChange = e => {
      setValue(e.target.checked);
    };

    const onBlur = () => {
      table.options.meta?.updateData(index, id, value);
    };

    // If the initialValue is changed external, sync it up with our state
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    return (
      <input
        id={`${index}-${getIndex()}`}
        type="checkbox"
        checked={value}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown(index, getIndex)}
        onClick={e => e.target?.select()}
      />
    );
  },
};

const COLUMN_TYPES = {
  number: defaultColumn(DCT.NUMBER),
  boolean: booleanColumn,
  date: defaultColumn(DCT.DATE),
};

const ARTable = memo(function ARTable({data, setSaveData}) {
  const columns = useMemo(
    () =>
      [
        {name: 'emClient', type: 'number'},
        {name: 'emCode', type: 'number'},
        'emName',
        {name: 'emDept', type: 'number'},
        {name: 'emMgr', type: 'boolean'},
        {name: 'emArbal', type: 'number'},
        {name: 'emMarbal', type: 'number'},
        'emElookup',
        'emTitle',
        {name: 'emTarget', type: 'number'},
        {name: 'emDefault', type: 'boolean'},
        {name: 'emInact', type: 'boolean'},
        {name: 'emDisc', type: 'number'},
        {name: 'emEo', type: 'number'},
        'emOppname',
        {name: 'emMaxbon', type: 'number'},
        {name: 'emSalary', type: 'number'},
        {name: 'emMeo', type: 'number'},
        {name: 'emPthres', type: 'number'},
        {name: 'emPshare', type: 'number'},
        {name: 'emMtarget', type: 'number'},
        {name: 'emFloc', type: 'boolean'},
        {name: 'emCloc', type: 'boolean'},
        {name: 'emCeloc', type: 'boolean'},
        {name: 'emMloc', type: 'boolean'},
        {name: 'emPloc', type: 'boolean'},
        {name: 'emPetloc', type: 'boolean'},
        {name: 'emEloc', type: 'boolean'},
        {name: 'emMmaxbon', type: 'number'},
        {name: 'emAdate', type: 'date'},
        {name: 'emTdate', type: 'date'},
        {name: 'emCremar', type: 'number'},
        {name: 'emCremeo', type: 'number'},
        {name: 'emCremarm', type: 'number'},
        {name: 'emCremeom', type: 'number'},
        {name: 'emArevfi', type: 'number'},
        {name: 'emArevfm', type: 'number'},
        {name: 'emArevci', type: 'number'},
        {name: 'emArevcm', type: 'number'},
        'emGroup',
        'emAlias1',
        'emAlias2',
        'emAlias3',
        {name: 'emRrate', type: 'number'},
        {name: 'emRrrev', type: 'boolean'},
        {name: 'emRrtot', type: 'boolean'},
        'emComment',
      ].map(column => {
        if (column.type) {
          return {
            accessorKey: column.name,
            header: column.name,
            ...(COLUMN_TYPES[column.type] ?? {}),
          };
        }

        return {
          id: column,
          accessorKey: column,
          header: column,
        };
      }),
    []
  );

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    defaultColumn: defaultColumn(),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Provide our updateData function to our table meta
    meta: {
      updateData: (rowIndex, columnId, value) => {
        setSaveData(old =>
          old.map((row, index) => {
            if (index === rowIndex) {
              return {
                ...old[rowIndex],
                [columnId]: value,
              };
            }
            return row;
          })
        );
      },
    },
  });


  return (
    <TableContainer height="36em" sx={{overflow: 'auto'}}>
      <Table size="sm">
        <Thead position="sticky" top={0} bgColor={'#FFFFFF'}>
          {table.getHeaderGroups().map(headerGroup => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <Th key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder ? null : <div role="button" onClick={header.column.getToggleSortingHandler()}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <div style={{float: 'right'}}>
                          {header.column.getIsSorted() ? (
                            header.column.getIsSorted() === "desc" ? (
                              <FaLongArrowAltDown aria-label="sorted descending" />
                            ) : (
                              <FaLongArrowAltUp aria-label="sorted ascending" />
                            )
                          ) : null}
                        </div>
                    </div>
                    }
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map(row => {
            return (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => {
                  return (
                    <Td key={cell.id} paddingTop={1} paddingBottom={1} borderRight="1px solid var(--chakra-colors-gray-100)">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
});
